import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import {
  PhoneCall, PhoneIncoming, PhoneOutgoing, PhoneMissed, Loader2, RefreshCw,
  Link2, FileAudio, Search, X, UserRound, ChevronDown, Sparkles, EyeOff, AlertTriangle,
} from 'lucide-react';

const fmtDur = (s) => {
  const m = Math.floor((s || 0) / 60);
  const sec = (s || 0) % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

const DirIcon = ({ call }) => {
  if (call.direction === 'in' && !call.answered) return <PhoneMissed className="w-3.5 h-3.5 text-red-400" />;
  return call.direction === 'in'
    ? <PhoneIncoming className="w-3.5 h-3.5 text-sky-400" />
    : <PhoneOutgoing className="w-3.5 h-3.5 text-emerald-400" />;
};

function AssignPopover({ call, onDone }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.searchLinkTargets(q.trim(), '').then(r => setResults(r.data.results || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const assign = async (pid) => {
    setBusy(true);
    try {
      const r = await api.webexAssignCall(call.id, pid);
      toast.success(`Call logged on ${r.data.project_name}${r.data.phone_saved ? ` — Nummer bei ${r.data.phone_saved_to || 'Owner-Kontakt'} gespeichert` : ''}`);
      setOpen(false);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not assign'); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`call-assign-btn-${call.id}`}
        className="flex items-center gap-1 text-[11px] border border-border rounded-sm px-2 py-1 text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors">
        <Link2 className="w-3 h-3" /> Assign to deal
      </button>
    );
  }
  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <div className="relative">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search deal…"
            data-testid={`call-assign-search-${call.id}`}
            className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs pl-7 w-52" />
        </div>
        <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground p-1"><X className="w-3.5 h-3.5" /></button>
      </div>
      {results.length > 0 && (
        <div className="absolute z-20 mt-1 w-64 max-h-52 overflow-y-auto bg-card border border-border rounded-sm shadow-lg">
          {results.map(p => (
            <button key={p.id} onClick={() => assign(p.id)} disabled={busy}
              data-testid={`call-assign-result-${p.id}`}
              className="block w-full text-left px-2.5 py-1.5 text-xs text-foreground hover:bg-secondary transition-colors">
              {p.name}{p.random_project_name ? <span className="text-muted-foreground"> · {p.random_project_name}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactAssignPopover({ call, onDone }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.getContacts(q.trim()).then(r => setResults((r.data.contacts || []).slice(0, 8))).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const assign = async (cid) => {
    setBusy(true);
    try {
      const r = await api.webexAssignCallContact(call.id, cid);
      const extras = [
        r.data.auto_assigned_project ? `Deal „${r.data.auto_assigned_project}" automatisch verknüpft` : '',
        r.data.phone_saved ? 'Nummer im Kontakt gespeichert (künftige Calls matchen automatisch)' : '',
      ].filter(Boolean).join(' — ');
      toast.success(`Call mit ${r.data.contact_name} verknüpft${extras ? ` — ${extras}` : ''}`, { duration: 7000 });
      setOpen(false);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Zuweisung fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`call-contact-btn-${call.id}`}
        className="flex items-center gap-1 text-[11px] border border-border rounded-sm px-2 py-1 text-muted-foreground hover:text-foreground hover:border-sky-500/50 transition-colors">
        <UserRound className="w-3 h-3" /> Kontakt zuweisen
      </button>
    );
  }
  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <div className="relative">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Kontakt suchen…"
            data-testid={`call-contact-search-${call.id}`}
            className="bg-secondary border-border text-foreground rounded-sm h-7 text-xs pl-7 w-52" />
        </div>
        <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground p-1"><X className="w-3.5 h-3.5" /></button>
      </div>
      {results.length > 0 && (
        <div className="absolute z-20 mt-1 w-64 max-h-52 overflow-y-auto bg-card border border-border rounded-sm shadow-lg">
          {results.map(c => (
            <button key={c.id} onClick={() => assign(c.id)} disabled={busy}
              data-testid={`call-contact-result-${c.id}`}
              className="block w-full text-left px-2.5 py-1.5 text-xs text-foreground hover:bg-secondary transition-colors">
              {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company}
              <span className="text-muted-foreground"> · {c.type || 'Kontakt'}{c.company ? ` · ${c.company}` : ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Reconnect the caller's Plaud account (OAuth) — used wherever an expired token surfaces.
export async function plaudReconnect() {
  try {
    const r = await api.plaudConnect();
    window.location.href = r.data.auth_url;
  } catch (err) { toast.error(err.response?.data?.detail || 'Plaud-Verbindung konnte nicht gestartet werden'); }
}

function PlaudLink({ call, expanded, onToggleExpand, onDone }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [recs, setRecs] = useState(null);
  const [recsError, setRecsError] = useState(null);
  const [busy, setBusy] = useState(false);

  const openList = () => {
    setOpen(true);
    if (items === null) api.webexPlaudItems().then(r => setItems(r.data.items || [])).catch(() => setItems([]));
    if (recs === null) api.getPlaudRecordings(14)
      .then(r => { setRecs((r.data || []).filter(x => !x.imported)); setRecsError(null); })
      .catch(err => {
        // never swallow this silently — an expired token made the newest recordings vanish (Max, 17.09.2026)
        setRecs([]);
        setRecsError({ status: err.response?.status, text: err.response?.data?.detail || 'Plaud-Aufnahmen konnten nicht geladen werden' });
      });
  };
  const reload = () => { setRecs(null); setItems(null); openList(); };

  const link = async (pid) => {
    setBusy(true);
    try {
      const r = await api.webexLinkPlaud(call.id, pid);
      if (r.data?.warning) toast.warning(r.data.warning, { duration: 10000 });
      else toast.success(pid ? 'Plaud-Notiz an Call angehängt' : 'Plaud-Notiz entfernt');
      setOpen(false);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  const importAndLink = async (rec) => {
    setBusy(true);
    try {
      const r = await api.importPlaudRecording(rec.id, rec.name, rec.created_at);
      const r2 = await api.webexLinkPlaud(call.id, r.data.id);
      if (r2.data?.warning) toast.warning(r2.data.warning, { duration: 10000 });
      else toast.success('Aufnahme importiert & an Call angehängt');
      setOpen(false);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import fehlgeschlagen'); }
    finally { setBusy(false); }
  };

  if (call.plaud_note) {
    return (
      <span className="flex items-center gap-1">
        <button onClick={onToggleExpand} data-testid={`call-plaud-badge-${call.id}`}
          className="flex items-center gap-1 text-[11px] border border-violet-500/40 bg-violet-500/10 text-violet-300 rounded-sm px-2 py-1 hover:bg-violet-500/20 transition-colors max-w-[220px]">
          <FileAudio className="w-3 h-3 shrink-0" />
          <span className="truncate">{call.plaud_note.title}</span>
          <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        <button onClick={() => link('')} title="Plaud-Notiz entfernen" data-testid={`call-plaud-remove-${call.id}`}
          className="text-muted-foreground hover:text-red-400 p-0.5 transition-colors"><X className="w-3 h-3" /></button>
      </span>
    );
  }
  if (!open) {
    return (
      <button onClick={openList} data-testid={`call-plaud-link-btn-${call.id}`}
        className="flex items-center gap-1 text-[11px] border border-border rounded-sm px-2 py-1 text-muted-foreground hover:text-violet-300 hover:border-violet-500/50 transition-colors">
        <FileAudio className="w-3 h-3" /> Plaud-Notiz
      </button>
    );
  }
  return (
    <div className="relative">
      <button onClick={() => setOpen(false)} className="flex items-center gap-1 text-[11px] border border-violet-500/50 rounded-sm px-2 py-1 text-violet-300">
        <FileAudio className="w-3 h-3" /> Plaud-Notiz <X className="w-3 h-3" />
      </button>
      <div className="absolute right-0 z-20 mt-1 w-72 max-h-72 overflow-y-auto bg-card border border-border rounded-sm shadow-lg"
        data-testid={`call-plaud-list-${call.id}`}>
        {recsError && (
          <div className="px-2.5 py-2 border-b border-amber-500/30 bg-amber-500/10" data-testid={`call-plaud-error-${call.id}`}>
            <p className="text-[11px] text-amber-300">{recsError.text}</p>
            <div className="flex items-center gap-2 mt-1.5">
              {recsError.status === 409 && (
                <button onClick={plaudReconnect} data-testid={`call-plaud-reconnect-${call.id}`}
                  className="text-[11px] border border-amber-500/50 text-amber-200 hover:bg-amber-500/20 rounded-sm px-2 py-0.5">Plaud neu verbinden</button>
              )}
              <button onClick={reload} className="text-[11px] text-muted-foreground hover:text-foreground">Erneut laden</button>
            </div>
          </div>
        )}
        {(recs || []).length > 0 && (
          <>
            <p className="px-2.5 pt-2 pb-1 text-[9px] uppercase tracking-wider text-violet-300/80">Neueste Plaud-Aufnahmen</p>
            {recs.map(rec => (
              <button key={rec.id} onClick={() => importAndLink(rec)} disabled={busy}
                data-testid={`call-plaud-rec-${rec.id}`}
                className="block w-full text-left px-2.5 py-1.5 hover:bg-secondary transition-colors">
                <span className="text-xs text-foreground block truncate">{rec.name}</span>
                <span className="text-[10px] text-muted-foreground block truncate">
                  {rec.created_at ? new Date(rec.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                  {rec.duration_min ? ` · ${rec.duration_min} min` : ''} · importieren & anhängen
                </span>
              </button>
            ))}
            <div className="border-t border-border/60" />
            <p className="px-2.5 pt-2 pb-1 text-[9px] uppercase tracking-wider text-muted-foreground/70">Bereits importierte Notizen</p>
          </>
        )}
        {items === null || recs === null ? (
          <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 && (recs || []).length === 0 ? (
          <p className="px-2.5 py-2 text-[11px] text-muted-foreground">{recsError ? 'Keine bereits importierten Notizen vorhanden.' : 'Keine Plaud-Aufnahmen der letzten 14 Tage und keine importierten Notizen gefunden.'}</p>
        ) : items.map(it => (
          <button key={it.id} onClick={() => link(it.id)} disabled={busy}
            data-testid={`call-plaud-item-${it.id}`}
            className="block w-full text-left px-2.5 py-1.5 hover:bg-secondary transition-colors">
            <span className="text-xs text-foreground block truncate">{it.subject}</span>
            <span className="text-[10px] text-muted-foreground block truncate">
              {it.received_at ? new Date(it.received_at).toLocaleDateString('de-DE') : ''} {it.preview ? `· ${it.preview}` : ''}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CallAnalyticsPage() {
  const { isAdmin } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [analyzing, setAnalyzing] = useState('');
  const [expandedPlaud, setExpandedPlaud] = useState('');
  const [plaud, setPlaud] = useState(null);
  useEffect(() => { api.getPlaudSettings().then(r => setPlaud(r.data)).catch(() => setPlaud(null)); }, []);

  const load = useCallback(() => {
    Promise.all([
      api.webexAnalytics(days),
      api.webexStatus(),
    ]).then(([a, s]) => {
      setData(a.data); setStatus(s.data);
    }).catch(() => toast.error('Could not load call analytics'))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.webexSync();
      load();
      if (r.data?.last_error) toast.error('Sync fehlgeschlagen — bitte die Webex-Verbindung prüfen');
      else toast.success('Sync complete');
    }
    catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const backfill = async () => {
    setSyncing(true);
    try {
      const r = await api.webexBackfill(48);
      load();
      const d = r.data || {};
      if (d.errors?.length) toast.warning(`${d.new_calls} Anruf(e) nachgeladen — ${d.errors.length} Zeitfenster fehlgeschlagen (${d.errors[0]})`);
      else toast.success(`${d.new_calls} fehlende(r) Anruf(e) der letzten 48 h nachgeladen (${d.fetched} Datensätze geprüft)`);
    }
    catch (err) { toast.error(err.response?.data?.detail || 'Nachladen fehlgeschlagen'); }
    finally { setSyncing(false); }
  };

  const analyzeNote = async (callId) => {
    setAnalyzing(callId);
    try {
      const r = await api.analyzeCallNote(callId);
      if (r.data.analyzed) {
        const a = r.data.analysis;
        const lbl = { rejection: 'Absage erkannt', interest: 'Interesse erkannt', not_reached: 'Nicht erreicht', other: 'Kein klares Ergebnis' }[a.outcome];
        toast.success(`${lbl}${a.reason ? ` — ${a.reason}` : ''}`,
          { description: a.outcome === 'rejection' ? 'Als negative Antwort verbucht + Pass-Vorschlag erstellt' : a.outcome === 'interest' ? 'Als positive Antwort verbucht' : a.outcome === 'not_reached' ? 'Als Anrufversuch gezählt' : undefined });
        load();
      } else {
        toast.error(r.data.detail || 'Auswertung nicht möglich');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Auswertung fehlgeschlagen'); }
    finally { setAnalyzing(''); }
  };

  const markPrivate = async (c) => {
    if (!window.confirm('Diesen Call als „Privat, nicht OLTA-relevant" markieren? Er verschwindet aus allen Listen und Statistiken.')) return;
    try {
      const r = await api.webexMarkCallPrivate(c.id);
      toast.success(r.data.message || 'Als privat markiert');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const timeline = data?.timeline || [];
  const maxDay = Math.max(1, ...timeline.map(t => t.inbound + t.outbound));

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="call-analytics-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-emerald-400" strokeWidth={1.5} /> Call Analytics
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Webex Calling — auto-tracked calls, matched to deals as engagement
          </p>
        </div>
        <span className="ml-auto flex items-center gap-2">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} data-testid={`calls-days-${d}`}
              className={`text-xs border rounded-sm px-2.5 py-1 transition-colors ${days === d ? 'border-primary/60 bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground'}`}>
              {d}d
            </button>
          ))}
          {status?.connected && (
            <Button size="sm" variant="outline" onClick={syncNow} disabled={syncing} data-testid="calls-sync-btn"
              className="h-7 text-xs rounded-sm border-border">
              {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Sync
            </Button>
          )}
          {status?.connected && (
            <Button size="sm" variant="outline" onClick={backfill} disabled={syncing} data-testid="calls-backfill-btn"
              title="Nach einem Aussetzer: alle Anrufe der letzten 48 Stunden aus Webex nachladen (weiter zurück liefert Webex keine Daten)"
              className="h-7 text-xs rounded-sm border-border">
              Letzte 48 h nachladen
            </Button>
          )}
        </span>
      </div>

      {plaud && (!plaud.connected || plaud.needs_reconnect) && (
        <div className="border border-violet-500/30 bg-violet-500/10 rounded-sm px-4 py-3 text-sm text-violet-200 flex items-center gap-3 flex-wrap"
          data-testid="calls-plaud-reconnect-banner">
          <FileAudio className="w-4 h-4 shrink-0" />
          <span className="flex-1 min-w-[240px]">
            {plaud.connected
              ? 'Deine Plaud-Verbindung ist abgelaufen — neue Aufnahmen werden nicht mehr geladen, bis du sie neu verbindest.'
              : 'Plaud ist für dein Konto nicht verbunden — neue Aufnahmen können nicht an Calls angehängt werden.'}
          </span>
          <Button size="sm" onClick={plaudReconnect} data-testid="calls-plaud-reconnect-btn"
            className="h-7 text-xs rounded-sm bg-violet-600 hover:bg-violet-500 text-white">
            {plaud.connected ? 'Plaud neu verbinden' : 'Plaud verbinden'}
          </Button>
        </div>
      )}

      {!status?.connected && (
        <div className="border border-amber-500/30 bg-amber-500/10 rounded-sm p-4 text-sm text-amber-300"
          data-testid="calls-not-connected">
          Webex Calling is not connected yet.
          {isAdmin ? <> Connect it on the <Link to="/team" className="underline">Team page</Link> (Webex Calling card).</>
            : ' Ask an admin to connect it on the Team page.'}
        </div>
      )}

      {status?.connected && status?.last_error && (
        <div className="border border-red-500/40 bg-red-500/10 rounded-sm p-4 text-sm text-red-300"
          data-testid="calls-sync-error">
          <div className="font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
            {status.needs_reconnect
              ? 'Webex-Verbindung abgelaufen — Anrufe werden nicht mehr erfasst'
              : 'Letzter Webex-Sync fehlgeschlagen'}
          </div>
          <p className="text-xs mt-1.5 text-red-200/90">
            {status.needs_reconnect
              ? 'Die Authentifizierung ist fehlgeschlagen (Token abgelaufen). Neue Anrufe können erst wieder erfasst werden, nachdem die Verbindung neu hergestellt wurde.'
              : 'Der letzte Synchronisierungsversuch ist fehlgeschlagen — neue Anrufe erscheinen aktuell nicht.'}
          </p>
          <p className="text-[11px] mt-1.5 font-mono text-red-200/70 break-words" data-testid="calls-sync-error-detail">
            {status.last_error}
          </p>
          <p className="text-xs mt-2">
            {isAdmin
              ? <>Bitte auf der <Link to="/team" className="underline">Team-Seite</Link> (Karte „Webex Calling") die Verbindung neu herstellen.</>
              : 'Bitte einen Admin bitten, die Webex-Verbindung auf der Team-Seite neu herzustellen.'}
          </p>
          {status.last_sync_at && (
            <p className="text-[11px] mt-1 text-red-200/60">
              Letzter erfolgreicher Sync: {new Date(status.last_sync_at).toLocaleString('de-DE')}
            </p>
          )}
        </div>
      )}

      {/* Per-user stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {(data?.users || []).map((u, i) => (
          <div key={i} className="border border-border rounded-sm bg-card p-3.5 space-y-2" data-testid={`calls-user-card-${i}`}>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground truncate">{u.name || '—'}</p>
              <span className="ml-auto text-[10px] font-mono text-muted-foreground">{u.line}</span>
            </div>
            <div className="flex items-end gap-3">
              <span className="text-2xl font-semibold text-foreground">{u.total}</span>
              <span className="text-[11px] text-muted-foreground mb-0.5">calls</span>
              <span className="ml-auto text-[11px] text-muted-foreground mb-0.5">{u.matched} on deals</span>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><PhoneIncoming className="w-3 h-3 text-sky-400" />{u.inbound}</span>
              <span className="flex items-center gap-1"><PhoneOutgoing className="w-3 h-3 text-emerald-400" />{u.outbound}</span>
              <span className="flex items-center gap-1"><PhoneMissed className="w-3 h-3 text-red-400" />{u.missed}</span>
              <span className="ml-auto">Ø {fmtDur(u.avg_duration_s)} · Σ {fmtDur(u.duration_s)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className="border border-border rounded-sm bg-card p-4" data-testid="calls-timeline">
          <p className="text-xs font-medium text-foreground mb-3">Calls per day (last {days}d)</p>
          <div className="flex items-end gap-[3px] h-24">
            {timeline.map(t => (
              <div key={t.date} className="flex-1 h-full flex flex-col justify-end gap-px group relative" title={`${t.date}: ${t.inbound} in / ${t.outbound} out`}>
                <div className="bg-emerald-500/70 rounded-t-[2px]" style={{ height: `${(t.outbound / maxDay) * 100}%` }} />
                <div className="bg-sky-500/70 rounded-t-[2px]" style={{ height: `${(t.inbound / maxDay) * 100}%` }} />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-2 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-sky-500/70 rounded-sm inline-block" /> inbound</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500/70 rounded-sm inline-block" /> outbound</span>
          </div>
        </div>
      )}

      {/* Recent calls */}
      <div className="border border-border rounded-sm bg-card" data-testid="calls-recent">
        <div className="px-4 py-3 border-b border-border">
          <p className="text-sm font-medium text-foreground">Recent calls</p>
        </div>
        {(data?.recent || []).length === 0 ? (
          <p className="px-4 py-6 text-xs text-muted-foreground">No calls tracked yet. Once Webex is connected, calls appear here ~5 minutes after they end.</p>
        ) : (
          <div className="divide-y divide-border">
            {(data?.recent || []).map(c => (
              <div key={c.id} className="px-4 py-2.5" data-testid={`recent-call-${c.id}`}>
                <div className="flex items-center gap-3 flex-wrap text-xs">
                  <DirIcon call={c} />
                  <span className="text-muted-foreground w-28 shrink-0">{fmtTime(c.start_time)}</span>
                  <span className="text-foreground font-medium w-24 shrink-0 truncate">{c.user_name || c.line_label}</span>
                  <span className="font-mono text-foreground">{c.contact_name || c.counterparty_number || 'unknown'}</span>
                  <span className="text-muted-foreground">{fmtDur(c.duration_s)}</span>
                  {c.is_internal && <span className="text-[10px] border border-border rounded px-1 text-muted-foreground">internal</span>}
                  <span className="ml-auto flex items-center gap-2">
                    {!c.contact_id && !c.is_internal && <ContactAssignPopover call={c} onDone={load} />}
                    <PlaudLink call={c} expanded={expandedPlaud === c.id}
                      onToggleExpand={() => setExpandedPlaud(x => (x === c.id ? '' : c.id))} onDone={load} />
                    {c.outcome_analysis ? (
                      <span title={c.outcome_analysis.reason || ''} data-testid={`call-outcome-${c.id}`}
                        className={`text-[10px] rounded-sm px-1.5 py-0.5 border ${
                          c.outcome_analysis.outcome === 'rejection' ? 'text-red-300 border-red-500/40 bg-red-500/10'
                          : c.outcome_analysis.outcome === 'interest' ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
                          : c.outcome_analysis.outcome === 'not_reached' ? 'text-amber-300 border-amber-500/40 bg-amber-500/10'
                          : 'text-muted-foreground border-border'}`}>
                        {{ rejection: 'Absage', interest: 'Interesse', not_reached: 'Nicht erreicht', other: 'Neutral' }[c.outcome_analysis.outcome]}
                      </span>
                    ) : c.plaud_note && c.project_id ? (
                      <button onClick={() => analyzeNote(c.id)} disabled={analyzing === c.id}
                        title="KI liest die Plaud-Notiz und verbucht das Ergebnis in den Outreach Statistics"
                        data-testid={`call-analyze-btn-${c.id}`}
                        className="text-[10px] border border-sky-700/50 text-sky-300 hover:bg-sky-500/10 disabled:opacity-50 rounded-sm px-1.5 py-0.5 inline-flex items-center gap-1 transition-colors">
                        {analyzing === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} KI auswerten
                      </button>
                    ) : null}
                    {c.status === 'matched' && c.project_id ? (
                      <>
                        {c.matched_via === 'landing_click' && (
                          <span data-testid={`call-landing-match-${c.id}`}
                            title="Automatisch zugeordnet: Der Anrufer hat kurz zuvor die Telefonnummer auf seiner Landing Page angetippt."
                            className="text-[10px] text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded px-1 py-0.5">
                            via Landing-Klick
                          </span>
                        )}
                        <Link to={`/projects/${c.project_id}#section-meetings`} data-testid={`call-deal-link-${c.id}`}
                          className="text-primary hover:underline">{c.project_name}</Link>
                      </>
                    ) : !c.is_internal ? (
                      <AssignPopover call={c} onDone={load} />
                    ) : (
                      <span className="text-[10px] text-muted-foreground">{c.status}</span>
                    )}
                    {isAdmin && (
                      <button onClick={() => markPrivate(c)} data-testid={`call-private-btn-${c.id}`}
                        title="Privat, nicht OLTA-relevant — Call aus allen Listen & Statistiken ausblenden"
                        className="text-muted-foreground/40 hover:text-red-400 transition-colors p-0.5">
                        <EyeOff className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </span>
                </div>
                {expandedPlaud === c.id && c.plaud_note && (
                  <div className="mt-2 ml-7 border border-violet-500/25 bg-violet-500/5 rounded-sm p-2.5"
                    data-testid={`call-plaud-summary-${c.id}`}>
                    <p className="text-[11px] text-foreground/90 whitespace-pre-wrap max-h-48 overflow-y-auto">{c.plaud_note.summary || '—'}</p>
                    <p className="text-[10px] text-muted-foreground mt-1.5">Verknüpft von {c.plaud_note.linked_by || '—'}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
